import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Standard modal/dialog keyboard behaviour: moves focus into the container
 * when it opens, traps Tab/Shift+Tab within it, closes on Escape, and
 * restores focus to whatever was focused before it opened.
 */
const useFocusTrap = (containerRef, isOpen, onClose) => {
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocused.current = document.activeElement;

    const container = containerRef.current;
    // Snapshot once on open — the focusable set inside these dialogs is
    // static while they're visible, so re-querying per keystroke would be
    // wasted DOM work.
    const focusables = container
      ? Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      : [];
    (focusables[0] || container)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const toRestore = previouslyFocused.current;
      if (toRestore && document.contains(toRestore)) toRestore.focus();
    };
  }, [isOpen, onClose, containerRef]);
};

export default useFocusTrap;
